import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, timer, TimeoutError } from 'rxjs';
import { catchError, retry, timeout } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import type { Stripe } from '@stripe/stripe-js';

export interface OhelCampTurnoProduct {
  turno: string;
  name: string;
  description: string | null;
  depositAmount: number;
  totalAmount: number;
}

export interface OhelCampProductsResponse {
  success: boolean;
  turnoProducts: OhelCampTurnoProduct[];
  error?: string;
}

export interface OhelCampCheckoutResponse {
  success: boolean;
  sessionId?: string;
  sessionUrl?: string;
  registrationId?: string;
  error?: string;
}

export interface OhelCampUploadResponse {
  success: boolean;
  documentId?: string;
  filename?: string;
  error?: string;
}

export interface OhelCampStripeError {
  type: 'validation' | 'network' | 'stripe' | 'server' | 'timeout';
  message: string;
  retryable: boolean;
}

const STRIPE_PUBLISHABLE_KEY = environment.stripePublishableKey;

@Injectable({
  providedIn: 'root',
})
export class OhelCampStripeService {
  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);
  private stripePromise: Promise<Stripe | null> | null = null;

  private readonly REQUEST_TIMEOUT = 30000;
  private readonly UPLOAD_TIMEOUT = 60000;
  private readonly MAX_RETRIES = 2;

  private async getStripe(): Promise<Stripe | null> {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    if (!this.stripePromise) {
      const { loadStripe } = await import('@stripe/stripe-js');
      this.stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
    }
    return this.stripePromise;
  }

  getProducts(): Observable<OhelCampProductsResponse> {
    return this.http
      .get<OhelCampProductsResponse>(
        `${environment.apiUrl}/ohel-camp/products`,
      )
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError((error) => this.handleError(error)),
      );
  }

  uploadDocument(
    file: File,
    tipoDocumento: string,
    soggetto: string,
    nomeCompleto: string,
  ): Observable<OhelCampUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tipoDocumento', tipoDocumento);
    formData.append('soggetto', soggetto);
    formData.append('nomeCompleto', nomeCompleto);

    return this.http
      .post<OhelCampUploadResponse>(
        `${environment.apiUrl}/ohel-camp/upload-document`,
        formData,
      )
      .pipe(
        timeout(this.UPLOAD_TIMEOUT),
        catchError((error) => this.handleError(error)),
      );
  }

  createCheckoutSession(
    data: Record<string, unknown>,
  ): Observable<OhelCampCheckoutResponse> {
    return this.http
      .post<OhelCampCheckoutResponse>(
        `${environment.apiUrl}/ohel-camp/create-checkout-session`,
        data,
      )
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        retry({
          count: this.MAX_RETRIES,
          delay: (error, retryCount) => {
            if (this.isRetryableError(error)) {
              return timer(1000 * retryCount);
            }
            return throwError(() => error);
          },
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  submitRegistrationWithoutPayment(
    data: Record<string, unknown>,
  ): Observable<{ success: boolean; registrationId?: string; error?: string }> {
    return this.http
      .post<{ success: boolean; registrationId?: string; error?: string }>(
        `${environment.apiUrl}/ohel-camp/submit-registration`,
        data,
      )
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        retry({
          count: this.MAX_RETRIES,
          delay: (error, retryCount) => {
            if (this.isRetryableError(error)) {
              return timer(1000 * retryCount);
            }
            return throwError(() => error);
          },
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  async processPayment(data: Record<string, unknown>): Promise<string> {
    return new Promise((resolve, reject) => {
      this.createCheckoutSession(data).subscribe({
        next: async (response) => {
          if (!response.success || !response.sessionId) {
            reject(
              this.createError(
                'server',
                response.error || 'Errore nella creazione della sessione',
                true,
              ),
            );
            return;
          }

          try {
            if (response.sessionUrl && isPlatformBrowser(this.platformId)) {
              this.document.location.href = response.sessionUrl;
              resolve(response.registrationId || '');
              return;
            }

            const stripe = await this.getStripe();
            if (!stripe) {
              throw this.createError(
                'stripe',
                'Impossibile caricare Stripe. Riprova piu tardi.',
                true,
              );
            }

            const { error } = await stripe.redirectToCheckout({
              sessionId: response.sessionId,
            });
            if (error) {
              throw this.createError(
                'stripe',
                error.message || 'Errore durante il redirect a Stripe',
                true,
              );
            }

            resolve(response.registrationId || '');
          } catch (error) {
            reject(error);
          }
        },
        error: (error) => reject(error),
      });
    });
  }

  private isRetryableError(
    error: HttpErrorResponse | TimeoutError,
  ): boolean {
    if (error instanceof TimeoutError) return true;
    if (!(error instanceof HttpErrorResponse)) return false;
    if (error.status === 0) return true;
    if (error.status === 502) return true;
    if (error.status === 503) return true;
    if (error.status === 504) return true;
    return false;
  }

  private handleError(
    error: HttpErrorResponse | TimeoutError,
  ): Observable<never> {
    let stripeError: OhelCampStripeError;

    if (error instanceof TimeoutError) {
      stripeError = this.createError(
        'timeout',
        'La richiesta ha impiegato troppo tempo. Verifica la connessione e riprova.',
        true,
      );
    } else if (error instanceof HttpErrorResponse && error.status === 0) {
      stripeError = this.createError(
        'network',
        'Errore di connessione. Verifica la tua connessione internet.',
        true,
      );
    } else if (error instanceof HttpErrorResponse && error.status === 400) {
      stripeError = this.createError(
        'validation',
        error.error?.error || 'Dati non validi. Controlla i campi del modulo.',
        false,
      );
    } else if (error instanceof HttpErrorResponse && error.status === 409) {
      stripeError = this.createError(
        'validation',
        error.error?.error ||
          "Esiste gia un'iscrizione con questa email per questo turno.",
        false,
      );
    } else if (error instanceof HttpErrorResponse && error.status === 502) {
      stripeError = this.createError(
        'stripe',
        'Errore nel servizio di pagamento. Riprova tra qualche minuto.',
        true,
      );
    } else {
      stripeError = this.createError(
        'server',
        (error instanceof HttpErrorResponse && error.error?.error) ||
          'Errore del server. Riprova piu tardi.',
        true,
      );
    }

    return throwError(() => stripeError);
  }

  private createError(
    type: OhelCampStripeError['type'],
    message: string,
    retryable: boolean,
  ): OhelCampStripeError {
    return { type, message, retryable };
  }
}
